import { Box, Button, Container, Typography } from "@mui/material";
import { LocalizationContext, ShareLinkObject } from "../../src/lib/context";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../api_config/firebase-configuration";
import { useRouter } from "next/router";
import React, { useContext, useEffect, useState } from "react";
import theme from "../../src/styles/theme";
import { ContactGrid } from "../../src/components/Profile/ContactGrid";
import { NextSeo } from "next-seo";
import { fetchProjects } from "../../src/lib/global";
import { useUserData } from "../../src/hooks/useUserData";
import { GetStaticPaths } from "next";
import ProfileCoverImage from "src/components/Profile/ProfileCoverImage";
import { useScreenSize } from "src/hooks/useScreenSize";
import ProjectsGrid from "src/components/Profile/ProjectsGrid";
import OtherDronersSection from "src/components/Sections/OtherDronersSection";
import { IndexDronersType } from "../../src/types/indexTypes";
import { getIndexDroners } from "../../src/lib/indexPage";
import { HomeTitleSection } from "src/components/CommonComponents/HomeTitleSection";
import PromotionalCard from "src/components/CommonComponents/PromotionalCard";
import ProfileText from "src/components/Profile/ProfileText";

export const getStaticPaths: GetStaticPaths = async () => {
  const users: any[] = [];
  const querySnapshot = await getDocs(collection(db, "usersPublic"));
  querySnapshot.forEach((doc) => {
    if (doc.data().username) users.push({ uid: doc.id, ...doc.data() });
  });

  const paths = users.map((user) => ({
    params: { username: user.username },
  }));
  return { paths, fallback: "blocking" };
};

export const getStaticProps = async ({
  params,
}: {
  params: { username: string };
}) => {
  const q = query(
    collection(db, "usersPublic"),
    where("username", "==", params.username)
  );

  let droner: any = null;
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    droner = { ...doc.data(), uid: doc.id };
  });

  delete droner?.userCreated;
  delete droner?.subscriptions;

  let allDroners: any[] = [];
  try {
    allDroners = await getIndexDroners();
    // Sort allDroners array in random Order
    allDroners.sort(() => Math.random() - 0.5);
  } catch (e) {
    console.error("droners error", e);
  }

  return {
    props: {
      droner,
      allDroners,
      username: params.username,
    },
    revalidate: 10,
  };
};

export default function Username({
  droner,
  username,
  allDroners,
}: {
  droner: any;
  username: string;
  allDroners: IndexDronersType[];
}) {
  const { localeStrings } = useContext(LocalizationContext);
  const localizedPageContent = localeStrings.profilePage;
  const { actDroner, user } = useUserData();
  const [canEdit, setCanEdit] = useState(false);
  const [infoOpen, setInfoOpen] = useState(true);
  const smAndDown = useScreenSize("sm", "down");
  const { query, isFallback, push } = useRouter();
  const [projects, setProjects] = useState<any[]>([]);
  const isFeedback = query.feedback || false;
  const prToOpen = query.project || "";
  const otc =
    typeof query.otc === "string" ? query.otc?.replace(" ", "") || "" : "";

  const { setShareLinkObject } = useContext(ShareLinkObject);

  const { uiGeneral } = useContext(LocalizationContext).localeStrings;
  const discountsPromoStrings = uiGeneral.promoCards.filter(
    (c) => c.promoId === "discounts"
  )[0];

  const hideBlueBox = () =>
    actDroner?.username !== droner?.username && droner?.profImage;

  useEffect(() => {
    setShareLinkObject({
      title: actDroner?.username,
      url: window.location.href,
    });
  }, [actDroner?.username, setShareLinkObject]);
  useEffect(() => {
    fetchProjects(droner?.uid).then((projects) => setProjects(projects));
  }, [droner?.uid]);

  if (isFallback) {
    return <div>{localeStrings.uiGeneral.loadingText}...</div>;
  } else if (!droner) {
    return (
      <Box
        my={20}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Typography textAlign="center">
          {localeStrings.uiGeneral.dronerNotFoundTitle}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button variant="contained" onClick={() => push("/")}>
            {localeStrings.uiGeneral.goToHomepageButton}
          </Button>
          <Button onClick={() => push("/droners")}>
            {localeStrings.uiGeneral.searchDronePilotsButton}
          </Button>
        </Box>
      </Box>
    );
  }

  useEffect(() => {
    setCanEdit(actDroner?.username === username);
  }, [actDroner, username]);

  /* useEffect(() => {
    const fetchProj = async () => {
      try {
        await fetchProjects(actDroner?.uid);
      } catch (e) {
        console.error(e);
      }
    };
    fetchProj().catch();
  }, [actDroner?.uid]); */

  const cutString = (s: string, n: number) => {
    const cut = s.indexOf(" ", n);
    if (cut == -1) return s;
    return s.substring(0, cut) + "...";
  };

  let dronerTitleSeo = droner?.username + localizedPageContent.dronerSeoTitle;
  let dronerBioSeo = "";
  if (droner?.displayName) {
    dronerTitleSeo = droner?.displayName + localizedPageContent.dronerSeoTitle;
  }
  if (droner?.bio) {
    dronerBioSeo = " | " + cutString(droner?.bio, 100);
  } else {
    dronerBioSeo = " | " + cutString(localizedPageContent.placeholder, 100);
  }

  return (
    <>
      <NextSeo
        title={dronerTitleSeo}
        description={
          localizedPageContent.dronerSeoDescriptionPart +
          " | " +
          droner?.address +
          dronerBioSeo
        }
        openGraph={{
          type: "profile",
          title:
            droner.displayName ??
            droner.username + localizedPageContent.dronerSeoTitle,
          description:
            localizedPageContent.dronerSeoDescriptionPart +
            ", " +
            droner?.address,
          profile: {
            username: droner?.displayName ?? droner?.username,
          },
          images: [
            {
              url: droner?.profImage ?? "/assets/profile_placeholder.svg",
              alt: droner?.displayName ?? droner?.username,
            },
          ],
        }}
        twitter={{
          handle: "@handle",
          site: "@site",
          cardType: "summary_large_image",
        }}
      />
      <Box>
        <ProfileCoverImage
          username={droner?.username}
          coverImage={droner?.coverImage}
          canEdit={canEdit}
          user={user}
        />

        <Container
          fixed
          sx={{
            display: "flex",
            pb: 5,
            flexDirection: { xs: "column", sm: "row" },
            maxWidth: "1248px !important",
            columnGap: "32px",
          }}
        >
          {/* CONTACT INFO SECTION */}
          <Box
            sx={{
              alignSelf: "flex-start",
              display: "flex",
              justifyContent: "center",
              width: 419,
              [theme.breakpoints.down("sm")]: {
                width: "100%",
                mt: 10,
              },
              [theme.breakpoints.up("sm")]: {
                mt: -10,
                top: 70,
                position: "sticky",
              },
            }}
          >
            <ContactGrid
              droner={droner}
              localizedPageContent={localizedPageContent}
              canEdit={canEdit}
              setInfoOpen={(bol) => setInfoOpen(bol)}
              infoOpen={infoOpen}
            />
          </Box>
          {/* PROJECTS SECTION */}
          <Box>
            <ProfileText />
            <Box sx={{ display: "flex" }}>
              {!smAndDown && !hideBlueBox() && (
                // {!smAndDown && (
                <ProjectsGrid
                  canEdit={canEdit}
                  projects={projects}
                  localizedPageContent={localizedPageContent}
                  prToOpen={prToOpen}
                  isFeedback={isFeedback}
                  otc={otc}
                />
              )}

              {smAndDown && !infoOpen && (
                <ProjectsGrid
                  canEdit={canEdit}
                  projects={projects}
                  localizedPageContent={localizedPageContent}
                  prToOpen={prToOpen}
                  isFeedback={isFeedback}
                  otc={otc}
                />
              )}
            </Box>
          </Box>
        </Container>
        <Container
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 5,
            mb: 5,
            mt: 5,
            maxWidth: "1248px !important",
          }}
        >
          <Box className="flex col" sx={{ mb: 3, px: 0 }}>
            <HomeTitleSection
              link="/droners"
              title="Ismerd meg további drónpilótáinkat!"
              subtitle="Válogass drónpilótáink közül, hasonlítsd össze őket, és találd meg a tökéletes jelöltet!"
              itemQuantity={allDroners?.length}
            />
            <OtherDronersSection allDroners={allDroners} />
          </Box>
          <Box sx={{ mb: 8 }}>
            <PromotionalCard
              title={discountsPromoStrings.title}
              content={discountsPromoStrings.description}
              imgUrl="/assets/discounts_cover_img.webp"
              btnContent={discountsPromoStrings.buttonText}
              link="/discounts"
              left
              dark
            />
          </Box>
        </Container>
      </Box>
    </>
  );
}
